import DigiobcanApi, {
  type Issue,
  type IssueStatement,
} from '../../../../api/DigiobcanApi';
import {
  getReportStatusMeta,
  reportStatusAvatarSx,
  ReportStatusDot,
} from '../../../../components/AdminReportStatusItem';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import { Link } from '../../../../components/Link';
import {
  userInitials,
  userLabel,
} from '../../../../components/UserAvatarStack';
import { adminGetIssuePossibleDuplicates } from '../../../../features/queries/adminGetIssuePossibleDuplicates';
import { adminGetIssueStatements } from '../../../../features/queries/adminGetIssueStatements';
import { adminGetReportDuplicates } from '../../../../features/queries/adminGetReportDuplicates';
import {
  getReportCategories,
  useDefaultReportCategoryId,
} from '../../../../features/queries/getReportCategories';
import { useReportCategoryMembership } from '../../../../features/queries/meGetReportCategories';
import { shortId } from '../../../../lib/ids';
import { useM, useQ } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import {
  __,
  formatDateTime,
  toastError,
  toastSuccess,
} from '../../../../utils';
import DecisionChip from './DecisionChip';
import DecisionSwitch from './DecisionSwitch';
import { PRIMARY_SWITCH_SX } from './DecisionSwitch';
import IssueAssignee from './IssueAssignee';
import IssueDuplicates from './IssueDuplicates';
import IssueRemove from './IssueRemove';
import IssueRetake from './IssueRetake';
import IssueSection from './IssueSection';
import IssueStatementHistory, {
  StatementEditedLabel,
} from './IssueStatementHistory';
import IssueStatementSection, {
  type PendingFile,
} from './IssueStatementSection';
import StatementApproval from './StatementApproval';
import StatementRevisionApproval from './StatementRevisionApproval';
import StatementRevisionDialog from './StatementRevisionDialog';
import {
  buildDecision,
  initialDecisionState,
  type IssueDecisionState,
} from './issueDecision';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha, type Theme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Collapse,
  Stack,
  StatusChip,
  Switch,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  issue: Issue;
  /**
   * Ostatné nálezy toho istého podnetu. Slúžia na dve veci, ktoré samotný
   * nález nevie: povedať pri presunutom náleze, kde sa podnet rieši teraz,
   * a neponúknuť pri prekategorizovaní kategóriu, ktorú podnet už má.
   */
  siblings?: Issue[];
  onDone: () => Promise<unknown>;
  compact?: boolean;
  /**
   * Má byť karta po otvorení stránky rozbalená? Rozhoduje o tom stránka, nie
   * nález - je to vec pohľadu na celý podnet (čo z neho chcem vidieť hneď),
   * nie vlastnosť nálezu.
   *
   * Neskoršia zmena na `true` kartu **otvorí** (odkaz na konkrétny nález), ale
   * zmena na `false` ju nezavrie - to, čo si úradník rozbalil, mu nikto
   * pod rukami zatvárať nebude.
   */
  defaultOpen?: boolean;
  /**
   * Podnet je archivovaný. Príznak nesie stránka, nie nález: je to vlastnosť
   * podnetu a `Issue` o nej nevie. Rozhodovanie tým celé zhasne
   * ([[buildDecision]]), rovnako ako u presunutého nálezu.
   */
  archived?: boolean;
  /**
   * Nález leží v kategórii, ktorú prihlásený úradník **len vidí**. Nesie to
   * stránka rovnako ako `archived`: odpoveď drží zoznam `meGetReportCategories`
   * a nie nález sám. Rozhodovanie tým celé zhasne ([[buildDecision]]).
   */
  unmanaged?: boolean;
};

/**
 * Odkaz v modrom informačnom bloku má tvar chipu, nie tlačidla: nič nemení,
 * len prenáša na druhú stranu vzťahu duplicity. Používajú ho oba smery - pruh
 * „som duplikát" aj zoznam duplikátov pod vyjadrením - takže je to jeden tvar
 * na jednom mieste. Červená v tejto appke patrí potvrdzovaciemu tlačidlu
 * v pätičke.
 */
const CHIP_LINK_SX = {
  color: 'info.dark',
  backgroundColor: (theme: Theme) => alpha(theme.palette.info.main, 0.14),
  border: 'none',
  '&:hover': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.info.main, 0.22),
  },
};

const IssueDetail: React.FC<Props> = ({
  issue,
  siblings = [],
  onDone,
  compact = false,
  defaultOpen = true,
  archived = false,
  unmanaged = false,
}) => {
  const { logger } = useLogger();
  const meta = getReportStatusMeta(issue.status);

  // Zbalenie sa týka len karty. Na mobile je nález celá obrazovka, na ktorú sa
  // úradník preklikol - tam nie je čo zbaľovať.
  const [expanded, setExpanded] = React.useState(compact || defaultOpen);

  React.useEffect(() => {
    if (defaultOpen) setExpanded(true);
  }, [defaultOpen]);

  // Čakáreň: kategória, do ktorej podnet padne, kým ho niekto nezaradí.
  // Nález sa v nej neprijíma, len posiela ďalej - viď `initialDecisionState`.
  // Ktorá to je, hovorí príznak `is_default` v databáze; kým sa zoznam
  // kategórií nenačíta, je to `undefined` a obrazovka sa správa ako nad
  // bežnou kategóriou - to je fail-open, tvrdú poistku drží API.
  const defaultCategoryId = useDefaultReportCategoryId();
  const isDefaultCategory = issue.category_id === defaultCategoryId;

  // Vyjadrenia tohto úradníka v tejto kategórii čakajú na schválenie? Príznak
  // je na jeho členstve, nie na náleze - ten istý nález rieši kolega bez neho.
  const membership = useReportCategoryMembership()(issue.category_id);

  const [state, setState] = React.useState<IssueDecisionState>(() =>
    initialDecisionState({
      status: issue.status,
      categoryName: issue.category_name,
      parentId: issue.parent_id,
      hasPossibleDuplicates: !!issue.has_possible_duplicates,
      isDefaultCategory,
    }),
  );
  const [files, setFiles] = React.useState<PendingFile[]>([]);

  // Zahodenie rozhodnutia. Robí sa na dvoch miestach - pri prepnutí nálezu
  // a po uložení - a v oboch znamená to isté: prepínače, text vyjadrenia aj
  // prílohy sa vracajú do východzieho stavu pre ten nález, ktorý je na
  // obrazovke teraz.
  const resetDecision = React.useCallback(() => {
    setState(
      initialDecisionState({
        status: issue.status,
        categoryName: issue.category_name,
        parentId: issue.parent_id,
        hasPossibleDuplicates: !!issue.has_possible_duplicates,
        isDefaultCategory,
      }),
    );
    setFiles([]);
  }, [
    issue.status,
    issue.category_name,
    issue.parent_id,
    issue.has_possible_duplicates,
    isDefaultCategory,
  ]);

  // Prepnutie nálezu je nová obrazovka, nie nový render tej istej - rozhodnutie
  // sa preto zahadzuje. Prenesené prepínače by znamenali, že úradník potvrdí
  // na jednom náleze to, čo naklikal na inom.
  React.useEffect(() => {
    resetDecision();
  }, [issue.id, resetDecision]);

  // Kategórie, ktoré už s týmto podnetom niečo majú. Rozdelené podľa toho, čo
  // sa stane, keď ich úradník vyberie pri prekategorizovaní:
  //  - `taken` - nález tam je, API novú kategóriu ticho preskočí,
  //  - `moved` - nález odtiaľ raz presunuli, API to odmietne (slučka).
  const takenCategoryIds = siblings
    .filter((s) => s.id !== issue.id && s.status !== 'moved')
    .map((s) => s.category_id);
  // Slučka hrozí len tam, kde po presune nezostal živý nález. Keď v kategórii
  // medzitým iný leží, je to len „už to tam je" a API to ticho preskočí.
  const movedCategoryIds = siblings
    .filter(
      (s) =>
        s.id !== issue.id &&
        s.status === 'moved' &&
        !takenCategoryIds.includes(s.category_id),
    )
    .map((s) => s.category_id);

  const { data: statements = [], refetch: refetchStatements } = useQ(
    adminGetIssueStatements,
    { issueId: issue.id },
  );

  // Vyjadrenie v schvaľovaní - čakajúce alebo vrátené na úpravu. Na náleze
  // je najviac jedno (drží to unikátny index v DB).
  const statementInReview = statements.find(
    (statement) =>
      statement.approval === 'pending' || statement.approval === 'rejected',
  );

  // Úpravy zverejnených vyjadrení v schvaľovaní (moj-presov#18) - najviac
  // jedna na vyjadrenie, ale vyjadrení môže byť viac. Nález neblokujú,
  // `buildDecision` o nich preto nevie.
  const revisionsInReview = statements.flatMap((statement) =>
    (statement.revisions ?? [])
      .filter(
        (revision) =>
          revision.approval === 'pending' || revision.approval === 'rejected',
      )
      .map((revision) => ({ statement, revision })),
  );

  // Upraviť sa dá zverejnené vyjadrenie živého aj vyriešeného nálezu -
  // finálne stanovisko sa opravuje práve po vyriešení. Nie presunutého ani
  // duplikátu (ich vyjadrenia občan nevidí), nie v archivovanom podnete a nie
  // v kategórii, ktorú úradník len vidí. Vyjadrenie s úpravou v schvaľovaní
  // ďalšiu nedostane - API by vrátilo `STATEMENT_REVISION_PENDING`.
  const revisable =
    !archived &&
    !unmanaged &&
    (issue.status === 'open' ||
      issue.status === 'accepted' ||
      issue.status === 'resolved');
  const canEditStatement = (statement: IssueStatement) =>
    revisable &&
    !statement.deleted_at &&
    (!statement.approval || statement.approval === 'approved') &&
    !(statement.revisions ?? []).some(
      (revision) =>
        revision.approval === 'pending' || revision.approval === 'rejected',
    );
  const [revising, setRevising] = React.useState<IssueStatement | null>(null);

  // Odstránenie vyjadrenia (000039) - tie isté podmienky ako úprava: API
  // odmietne vyjadrenie v schvaľovaní aj to, ktorého úprava sa schvaľuje.
  const [deleting, setDeleting] = React.useState<IssueStatement | null>(null);
  // Odstránenie finálneho vyjadrenia vráti vyriešený nález do riešenia.
  const reopensIssue = !!deleting?.final && issue.status === 'resolved';
  const deleteMutation = useM(
    (statement: IssueStatement) =>
      DigiobcanApi.adminDeleteStatement(statement.statement_id),
    {
      onSuccess: async () => {
        await Promise.all([refetchStatements(), onDone()]);
        setDeleting(null);
        toastSuccess(
          reopensIssue
            ? __('Vyjadrenie bolo odstránené, nález je znova v riešení.')
            : __('Vyjadrenie bolo odstránené.'),
        );
      },
      onError: (error) => {
        logger.error('Failed to delete statement', {
          action: 'statementDelete',
          error,
          data: { statementId: deleting?.statement_id },
        });
        const name = error.details?.name;
        toastError(
          name === 'STATEMENT_DELETED' ||
            name === 'STATEMENT_NOT_PUBLISHED' ||
            name === 'STATEMENT_REVISION_PENDING'
            ? __(
                'S vyjadrením už medzitým niekto niečo urobil. Obnovte stránku.',
              )
            : name === 'REPORT_ARCHIVED'
              ? __('Podnet je archivovaný.')
              : __('Vyjadrenie sa nepodarilo odstrániť.'),
        );
      },
    },
  );

  const decision = buildDecision(
    {
      status: issue.status,
      categoryName: issue.category_name,
      parentId: issue.parent_id,
      hasPossibleDuplicates: !!issue.has_possible_duplicates,
      isDefaultCategory,
      movedCategoryIds,
      archived,
      unmanaged,
      requiresApproval: membership.requiresApproval,
      // Stav berie z vyjadrenia, ktoré je už načítané; kým sa nenačíta,
      // stačí vedieť, že nejaké v schvaľovaní je - zamyká to isté.
      statementInReview: issue.statement_in_review_id
        ? statementInReview?.approval === 'rejected'
          ? 'rejected'
          : 'pending'
        : undefined,
    },
    state,
  );

  const { data: candidates = [] } = useQ(
    adminGetIssuePossibleDuplicates,
    { issueId: issue.id },
    { enabled: decision.showDuplicates },
  );

  const { data: categories = [] } = useQ(getReportCategories, undefined, {
    enabled: decision.showRecategorize && state.recategorize,
  });

  // Nálezy z iných podnetov, ktoré sú zlúčené do tohto. Dizajn túto stranu
  // vzťahu nekreslí - kreslí len pruh „som duplikát" - ale úradník to potrebuje
  // vidieť: vyjadrenie, ktoré tu napíše, dostanú aj ich autori.
  const { data: reportDuplicates = [] } = useQ(adminGetReportDuplicates, {
    reportId: issue.report_id,
  });
  const mergedIntoThis = reportDuplicates.filter(
    (duplicate) => duplicate.parent_id === issue.id,
  );

  const patch = (changes: Partial<IssueDecisionState>) =>
    setState((prev) => ({ ...prev, ...changes }));

  const decideMutation = useM(
    async () => {
      if (!decision.payload) return;

      const { statement_id: statementId, approval } =
        await DigiobcanApi.adminIssueDecision(
          issue.id,
          undefined,
          decision.payload,
        );

      // Prílohy až po rozhodnutí: `decision` je jedna transakcia a súbory do
      // nej nepatria. Keď upload zlyhá, vyjadrenie existuje bez nich a dá sa
      // doplniť - opak (prílohy bez vyjadrenia) by bol sirotinec.
      if (statementId && files.length) {
        await DigiobcanApi.adminAddIssueStatementMedia(
          issue.id,
          statementId,
          undefined,
          { media: files.map((f) => f.file) } as never,
        );
      }

      // Vyjadrenia sú vlastná query - `onDone` obnovuje obálku a nálezy, takže
      // bez tohto by čerstvo uložené vyjadrenie pribudlo do zoznamu až po F5.
      await Promise.all([refetchStatements(), onDone()]);

      return approval;
    },
    {
      onSuccess: (approval) => {
        // Uložené vyjadrenie je už v histórii pod formulárom a prílohy na
        // serveri, takže pole aj zoznam súborov musia byť prázdne - inak
        // úradník pri ďalšom uložení pošle to isté druhý raz. Pri prijatí
        // nálezu to spraví aj `useEffect` vyššie (mení sa stav nálezu), pri
        // priebežnom vyjadrení sa stav nemení a text by v poli zostal.
        resetDecision();
        toastSuccess(
          approval === 'pending'
            ? __('Vyjadrenie bolo odoslané na schválenie.')
            : __('Rozhodnutie bolo uložené.'),
        );
      },
      onError: (error) => {
        logger.error('Failed to submit issue decision', {
          action: 'issueDecision',
          error,
          data: { issueId: issue.id, payload: decision.payload },
        });

        const name = error.details?.name;
        if (name === 'EXISTING_ISSUES_IN_CATEGORIES') {
          toastError(
            __(
              'Do kategórie, z ktorej už nález presunuli, ho priradiť nemožno. Musí si ho prevziať kolega, ktorý ju má na starosti.',
            ),
          );
        } else if (name === 'DUPLICATE_ISSUE_CATEGORY_MISMATCH') {
          toastError(
            __(
              'Duplicitu možno označiť len medzi nálezmi v tej istej kategórii.',
            ),
          );
        } else if (name === 'INVALID_DUPLICATE_ISSUE_STATUS') {
          toastError(
            __(
              'Duplikátom sa môže stať len podaný alebo prijatý nález. Vyriešený je uzavretý.',
            ),
          );
        } else if (name === 'INVALID_ISSUE_STATUS_UPDATE') {
          toastError(__('Stav nálezu sa medzitým zmenil. Obnovte stránku.'));
        } else if (name === 'STATEMENT_PENDING_APPROVAL') {
          toastError(
            __('Iné vyjadrenie k nálezu čaká na schválenie. Obnovte stránku.'),
          );
        } else if (name === 'STATEMENT_REQUIRED_FOR_APPROVAL') {
          toastError(
            __('Napíšte vyjadrenie - vyriešenie sa bez neho schváliť nedá.'),
          );
        } else {
          toastError(__('Rozhodnutie sa nepodarilo uložiť.'));
        }
      },
    },
  );

  const submitDecision = () => {
    if (!navigator.onLine) {
      toastError(__('Nie ste pripojený k internetu.'));
      return;
    }
    decideMutation.mutate(undefined);
  };

  /**
   * Obsah pätičky rozhodnutia na desktope - v karte vyjadrenia, alebo
   * samostatne, keď vyjadrenie nález nemá. Vľavo voľba, ktorá k pätičke patrí
   * (vyriešiť), vpravo čo sa stane a tlačidlo, ktoré to uloží naraz.
   */
  const decisionFooter = (extra?: React.ReactNode) => (
    <>
      {extra}
      <Box sx={{ flex: 1 }} />
      <Typography
        sx={{
          fontSize: 13,
          color: 'text.secondary',
          maxWidth: '60ch',
          textAlign: 'right',
        }}
      >
        {decision.sentences.join(' ')}
      </Typography>
      <Button
        disabled={decision.disabled || decideMutation.isPending}
        onClick={submitDecision}
      >
        {decision.label}
      </Button>
    </>
  );

  // Všetky vyjadrenia v jednom prúde, **od najnovšieho** - tak ich vracia
  // API. Najnovšie je to, čo úradník potrebuje vedieť pri rozhodovaní, takže
  // stojí hneď pod formulárom; staršie kolá sú čím ďalej, tým nižšie.
  const timeline = statements;

  // Formulár je len pre nález, o ktorom sa ešte rozhoduje. Uzavretý ho mal
  // dovtedy tiež - v režime „len na čítanie", ktorý nekreslil nič iné než to,
  // čo dnes stojí v časovej osi.
  const showStatementForm = decision.showStatement && !decision.readOnly;

  // Dátumy nálezu v hlavičke karty (návrh 1a) - kedysi samostatný riadok pod
  // ňou. Na mobile ostáva v hlavičke len posledná zmena, viac sa tam nezmestí.
  const headerDates = [
    `${__('Prijaté')} ${formatDateTime(new Date(issue.inserted_at))}`,
    issue.resolved_at &&
      `${__('Vyriešené')} ${formatDateTime(new Date(issue.resolved_at))}`,
    issue.updated_at &&
      `${__('Upravené')} ${formatDateTime(new Date(issue.updated_at))}`,
  ]
    .filter(Boolean)
    .join(' · ');

  // Hlavička karty: pruh so všetkým, čím sa nález menuje. Bodka
  // pred názvom nesie ten istý stav ako štítok vedľa - je to jediné, čo sa dá
  // zachytiť periférne, keď je kariet pod sebou päť.
  const header = (
    <Stack
      direction={compact ? 'column' : 'row'}
      alignItems={compact ? 'flex-start' : 'center'}
      gap={compact ? 0.5 : 1.25}
      role={compact ? undefined : 'button'}
      aria-expanded={compact ? undefined : expanded}
      onClick={compact ? undefined : () => setExpanded((value) => !value)}
      sx={{
        px: compact ? 0 : 2.25,
        ...(compact
          ? {}
          : {
              flex: 'none',
              py: 1.75,
              cursor: 'pointer',
              userSelect: 'none',
              backgroundColor: 'background.paper',
              // Linka patrí k rozhraniu hlavičky a tela - zbalená karta je
              // celá tým pruhom a linka by jej visela na spodnej hrane.
              borderBottom: (theme) =>
                expanded ? `1px solid ${theme.palette.divider}` : 'none',
            }),
      }}
    >
      <Stack direction='row' alignItems='center' gap={1.25} flexWrap='wrap'>
        {!compact && <ReportStatusDot status={issue.status} size={10} />}
        <Typography variant='subtitle1' fontWeight={700}>
          {issue.category_name}
        </Typography>
        <StatusChip tone={meta.tone}>{meta.title}</StatusChip>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
        >
          {issue.id}
        </Typography>
      </Stack>

      <Stack
        direction='row'
        alignItems='center'
        gap={1.25}
        sx={{ ml: compact ? 0 : 'auto' }}
      >
        <Typography variant='caption' color='text.secondary'>
          {compact
            ? formatDateTime(new Date(issue.updated_at ?? issue.inserted_at))
            : headerDates}
        </Typography>
        {!compact && (
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: 'text.secondary',
              transition: 'transform .15s',
              transform: expanded ? 'rotate(180deg)' : 'none',
            }}
          />
        )}
      </Stack>
    </Stack>
  );

  // Pridelenie konkrétnemu kolegovi (moj-presov#17). Meniť ho smie ten, komu
  // to povie server (`can_assign`); v archivovanom podnete a v kategórii,
  // ktorú úradník len vidí, zostane len informácia.
  const assignmentEditable = !!issue.can_assign && !archived && !unmanaged;

  /**
   * „Tento nález rieši Meno Priezvisko (ID)" - pás hneď pod hlavičkou karty,
   * mimo zbaľovania: kto nález rieši, má byť vidieť aj pri zbalenej karte.
   * Identifikátor je ten istý krátky, aký je v tabuľke členov kategórie;
   * bez mena v profile je identifikátor jediné, čo sa dá ukázať, a dvakrát
   * za sebou sa nepíše. Presunutý nález priradenie síce nesie, ale už ho
   * nikto nerieši.
   */
  const assignee = issue.status !== 'moved' ? issue.assignee : undefined;
  const assigneeHasName = !!(assignee?.name || assignee?.surname);
  const assigneeLine = assignee && (
    <Stack
      direction='row'
      alignItems='center'
      gap={1}
      sx={{
        px: compact ? 0 : 2.25,
        py: compact ? 0 : 1,
        ...(!compact && {
          borderTop: (theme: Theme) =>
            `1px solid ${theme.palette.dividerLight}`,
          backgroundColor: 'background.default',
        }),
      }}
    >
      <Avatar
        sx={{
          ...reportStatusAvatarSx(issue.status),
          width: 22,
          height: 22,
          fontSize: 10,
          fontWeight: 700,
        }}
      >
        {userInitials(assignee)}
      </Avatar>
      <Typography variant='body2' color='text.secondary' sx={{ minWidth: 0 }}>
        {__('Tento nález rieši')}{' '}
        <Box component='span' sx={{ color: 'text.primary', fontWeight: 600 }}>
          {userLabel(assignee)}
        </Box>
        {assigneeHasName && ` (${shortId(assignee.id)})`}
        {issue.assigned_to_me && ` - ${__('vy')}`}
      </Typography>
    </Stack>
  );

  // Telo je postupnosť krokov oddelených tichou linkou. Poradie aj to, ktorý
  // krok linku dostane, je dané dizajnom; prvý vykreslený ju nikdy nemá, preto
  // sa skladajú do poľa a nie priamo do JSX - inak by linka visela na vrchu
  // vždy, keď je predchádzajúci krok skrytý.
  //
  // Na desktope sa z krokov skladajú karty sekcií ([[IssueSection]], návrh
  // 1a). `bare` je krok, ktorý má rám sám (informačné a schvaľovacie bloky),
  // `desktop` vlastná podoba karty; `null` znamená, že ho kreslí iná karta.
  const steps: {
    key: string;
    node: React.ReactNode;
    divided?: boolean;
    bare?: boolean;
    desktop?: React.ReactNode | null;
  }[] = [];

  // Výber riešiteľa stojí navrchu: kto nález rieši, je prvá vec, ktorú
  // rozdeľovateľ nad novým nálezom robí. Ostatní vidia riešiteľa len
  // v pásiku `assigneeLine`.
  if (
    issue.category_assignment &&
    issue.status !== 'moved' &&
    assignmentEditable
  ) {
    steps.push({
      key: 'assignee',
      node: <IssueAssignee issue={issue} compact={compact} onDone={onDone} />,
    });
  }

  // Presunutý nález: iba informácia o presune a cesta späť. `buildDecision` mu
  // všetky kroky aj pätičku vypína, takže toto je celý jeho obsah.
  if (issue.status === 'moved') {
    // Kam presun viedol, sa nikde neukladá - nález nesie len svoj vlastný stav
    // a dátum zmeny. Vypísať sa preto dá to, čo je isté: kde sa podnet rieši
    // teraz. Presunuté nálezy tam nepatria (tie tiež odišli) a duplikáty tiež
    // nie - tie sa riešia v origináli, nie v tejto kategórii.
    const elsewhere = [
      ...new Set(
        siblings
          .filter(
            (s) =>
              s.id !== issue.id &&
              s.status !== 'moved' &&
              s.status !== 'duplicate',
          )
          .map((s) => s.category_name),
      ),
    ];

    steps.push({
      key: 'moved',
      bare: true,
      node: (
        <Stack
          direction={compact ? 'column' : 'row'}
          alignItems={compact ? 'stretch' : 'center'}
          gap={compact ? 1 : 1.5}
          sx={{
            p: 1.5,
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.info.surface}`,
            backgroundColor: 'info.surface',
          }}
        >
          <Stack gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body2' color='info.dark' fontWeight={500}>
              {`${__('Nález bol prekategorizovaný')} ${formatDateTime(
                new Date(issue.updated_at ?? issue.inserted_at),
              )}`}
            </Typography>
            <Typography variant='caption' color='info.dark'>
              {elsewhere.length
                ? `${__('Podnet sa rieši v')} ${
                    elsewhere.length === 1 ? __('kategórii') : __('kategóriách')
                  } ${elsewhere.join(', ')}.`
                : __('Podnet momentálne nemá nález v žiadnej kategórii.')}
            </Typography>
          </Stack>
          <Box sx={{ flex: 'none' }}>
            <IssueRetake
              issue={issue}
              onDone={onDone}
              archived={archived}
              readOnly={unmanaged}
            />
          </Box>
        </Stack>
      ),
    });
  }

  // Duplikát nemá rozhodovanie (`buildDecision` mu všetky kroky vypína), takže
  // toto je celý jeho obsah: koho duplikátom je a kadiaľ sa dostať k originálu.
  // Odkaz nevedie na podnet, ale priamo na rodičovský nález - to je to
  // miesto, kde sa vec naozaj rieši.
  if (issue.parent_id) {
    steps.push({
      key: 'duplicate-of',
      bare: true,
      node: (
        <Stack
          direction={compact ? 'column' : 'row'}
          alignItems={compact ? 'stretch' : 'center'}
          gap={compact ? 1 : 1.5}
          sx={{
            p: 1.5,
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.info.surface}`,
            backgroundColor: 'info.surface',
          }}
        >
          <Stack gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body2' color='info.dark' fontWeight={500}>
              {__('Označené ako duplikát')}
            </Typography>
            <Typography variant='caption' color='info.dark'>
              {`${__('Rieši sa v náleze')} ${issue.parent_id}${
                issue.parent_report_id
                  ? ` · ${__('podnet')} ${issue.parent_report_id}`
                  : ''
              }`}
            </Typography>
          </Stack>
          {issue.parent_report_id && (
            <Box sx={{ flex: 'none' }}>
              <Button
                variant='text'
                size='small'
                LinkComponent={Link}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore Button z knižnice neprepúšťa `to` do LinkComponent
                to={`/administracia/podnety/${shortId(
                  issue.parent_report_id,
                )}/nalezy/${shortId(issue.parent_id)}`}
                fullWidth={compact}
                sx={CHIP_LINK_SX}
              >
                {__('Otvoriť originál')}
              </Button>
            </Box>
          )}
        </Stack>
      ),
    });
  }

  // Duplikáty zlúčené do tohto nálezu sú kontext, nie rozhodnutie - stoja
  // preto nad vyjadrením, spolu s ostatnými informáciami o náleze.
  if (mergedIntoThis.length) {
    steps.push({
      key: 'merged',
      divided: true,
      desktop: (
        <IssueSection
          title={__('Zlúčené duplikáty')}
          description={__(
            'Tieto nálezy sa riešia spolu s týmto. Vyjadrenie sa zverejní aj pri nich.',
          )}
          control={
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {`${mergedIntoThis.length} ${
                mergedIntoThis.length === 1
                  ? __('nález')
                  : mergedIntoThis.length <= 4
                    ? __('nálezy')
                    : __('nálezov')
              }`}
            </Typography>
          }
          flush
        >
          {mergedIntoThis.map((duplicate) => (
            <Stack
              key={duplicate.id}
              direction='row'
              alignItems='center'
              gap={1.5}
              sx={{
                px: 2.5,
                py: 1.25,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  color: 'text.secondary',
                  fontFamily: 'ui-monospace, Menlo, monospace',
                }}
              >
                {duplicate.id}
              </Typography>
              <Typography variant='body2' sx={{ flex: 1, minWidth: 0 }}>
                {duplicate.category_name}
              </Typography>
              <Button
                variant='text'
                size='small'
                LinkComponent={Link}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore Button z knižnice neprepúšťa `to` do LinkComponent
                to={`/administracia/podnety/${shortId(
                  duplicate.report_id,
                )}/nalezy/${shortId(duplicate.id)}`}
              >
                {__('Otvoriť')} →
              </Button>
            </Stack>
          ))}
        </IssueSection>
      ),
      node: (
        <Stack gap={0.75}>
          <Typography variant='body2' fontWeight={500}>
            {__('Duplikáty tohto nálezu')}
          </Typography>
          {mergedIntoThis.map((duplicate) => (
            <Stack
              key={duplicate.id}
              direction='row'
              alignItems='center'
              gap={1.25}
              flexWrap='wrap'
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.info.surface}`,
                backgroundColor: 'info.surface',
              }}
            >
              <Typography
                variant='caption'
                sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
                color='info.dark'
              >
                {duplicate.id}
              </Typography>
              <Typography variant='body2' color='info.dark' sx={{ flex: 1 }}>
                {duplicate.category_name}
              </Typography>
              {/* Cieľom je ten duplikát, nie len jeho podnet - inak by
                  desktop doplnil do adresy prvý nález podnetu a úradník by
                  skončil pri niečom, na čo neklikol. */}
              <Button
                variant='text'
                size='small'
                LinkComponent={Link}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore Button z knižnice neprepúšťa `to` do LinkComponent
                to={`/administracia/podnety/${shortId(
                  duplicate.report_id,
                )}/nalezy/${shortId(duplicate.id)}`}
                sx={CHIP_LINK_SX}
              >
                {__('Otvoriť duplikát')}
              </Button>
            </Stack>
          ))}
        </Stack>
      ),
    });
  }

  // Finálne stanovisko vyriešeného nálezu ostáva na očiach, nie len v zbalenej
  // histórii: je to odpoveď úradu, kvôli ktorej sa na uzavretý nález pozerá.
  // Berie sa len zverejnené - stiahnuté či vrátené finálne vyjadrenie nález
  // nevyriešilo.
  const finalStatement =
    issue.status === 'resolved'
      ? statements.find(
          (statement) =>
            statement.final &&
            !statement.deleted_at &&
            (!statement.approval || statement.approval === 'approved'),
        )
      : undefined;

  if (finalStatement) {
    steps.push({
      key: 'final-statement',
      bare: true,
      divided: !!steps.length,
      node: (
        <Stack
          gap={0.75}
          sx={{
            p: 1.5,
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.success.surface}`,
            backgroundColor: 'success.surface',
          }}
        >
          <Stack direction='row' alignItems='baseline' gap={1} flexWrap='wrap'>
            <Typography variant='body2' color='success.dark' fontWeight={700}>
              {__('Finálne vyjadrenie')}
            </Typography>
            <Typography variant='caption' color='success.dark' sx={{ flex: 1 }}>
              {formatDateTime(new Date(finalStatement.inserted_at))}
              {!!finalStatement.revisions?.length && (
                <>
                  {' · '}
                  <StatementEditedLabel statement={finalStatement} />
                </>
              )}
            </Typography>
            <Typography variant='caption' color='success.dark'>
              {[finalStatement.author?.name, finalStatement.author?.surname]
                .filter(Boolean)
                .join(' ') || __('Úrad')}
            </Typography>
            {/* Tu a nie len v histórii: na vyriešenom náleze je toto blok,
            na ktorý sa úradník pozerá, a oprava stanoviska je hlavný dôvod,
            prečo úprava vznikla. */}
            {canEditStatement(finalStatement) && (
              <>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => setRevising(finalStatement)}
                >
                  {__('Upraviť')}
                </Button>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => setDeleting(finalStatement)}
                >
                  {__('Odstrániť')}
                </Button>
              </>
            )}
          </Stack>
          <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
            {finalStatement.text}
          </Typography>
          {finalStatement.media.map((media) => (
            <Typography
              key={media.media_id}
              variant='caption'
              component='a'
              href={media.public_url}
              target='_blank'
              rel='noopener noreferrer'
            >
              {media.original_filename ?? media.media_id}
            </Typography>
          ))}
        </Stack>
      ),
    });
  }

  // História vyjadrení nie je krok rozhodovania - kreslí sa **pod** pätičkou
  // (`history` nižšie). Tu zostáva len veta pre uzavretý nález bez stanoviska.
  if (!timeline.length && decision.readOnly && decision.showStatement) {
    steps.push({
      key: 'no-statement',
      bare: true,
      divided: !!steps.length,
      node: (
        <Stack
          sx={{
            p: 1.5,
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.info.surface}`,
            backgroundColor: 'info.surface',
          }}
        >
          <Typography variant='body2' color='info.dark'>
            {__('Nález bol uzavretý bez písomného stanoviska.')}
          </Typography>
        </Stack>
      ),
    });
  }

  // Vyjadrenie v schvaľovaní: schvaľovateľovi rozhodnutie, autorovi
  // vráteného úprava, ostatným vysvetlenie, prečo formulár vyjadrenia zmizol.
  // Stojí na vrchu - je to to, čo sa s nálezom práve deje.
  if (statementInReview) {
    steps.push({
      key: 'approval',
      bare: true,
      divided: !!steps.length,
      node: (
        <StatementApproval
          statement={statementInReview}
          canReview={!!issue.awaiting_my_approval}
          // Vrátené smie stiahnuť aj schvaľovateľ či správca, nielen autor -
          // inak by ho kolega, ktorý odišiel, nechal blokovať nález navždy.
          canWithdraw={
            !!statementInReview.mine ||
            membership.canApprove ||
            membership.manager
          }
          requiresApproval={membership.requiresApproval}
          readOnly={archived || unmanaged}
          compact={compact}
          onDone={() => Promise.all([refetchStatements(), onDone()])}
        />
      ),
    });
  }

  revisionsInReview.forEach(({ statement, revision }) => {
    steps.push({
      key: `revision-${revision.revision_id}`,
      bare: true,
      divided: !!steps.length,
      node: (
        <StatementRevisionApproval
          statement={statement}
          revision={revision}
          canReview={!!issue.awaiting_my_approval}
          canWithdraw={
            !!revision.mine || membership.canApprove || membership.manager
          }
          requiresApproval={membership.requiresApproval}
          readOnly={archived || unmanaged}
          compact={compact}
          onDone={() => Promise.all([refetchStatements(), onDone()])}
        />
      ),
    });
  });

  if (decision.showAccept) {
    steps.push({
      key: 'accept',
      desktop: (
        <IssueSection
          title={decision.acceptLabel}
          description={decision.acceptHint}
          control={
            <Switch
              checked={state.accept && !decision.acceptDisabled}
              disabled={decision.acceptDisabled}
              onChange={(_event, accept) => patch({ accept, resolve: false })}
              inputProps={{ 'aria-label': String(decision.acceptLabel) }}
              sx={PRIMARY_SWITCH_SX}
            />
          }
        />
      ),
      node: (
        <DecisionSwitch
          stacked={compact}
          framed={compact}
          label={decision.acceptLabel}
          hint={decision.acceptHint}
          disabled={decision.acceptDisabled}
          checked={state.accept && !decision.acceptDisabled}
          // Prijatie a vyriešenie sú dva kroky tej istej cesty: vypnutím
          // prijatia stráca „vyriešiť" zmysel, takže padá s ním.
          onChange={(accept) => patch({ accept, resolve: false })}
        />
      ),
    });
  }

  if (decision.showDuplicates) {
    steps.push({
      key: 'duplicates',
      divided: true,
      node: (
        <IssueDuplicates
          candidates={candidates}
          direction={state.duplicateDirection}
          picked={state.duplicatePicked}
          onDirectionChange={(duplicateDirection) =>
            patch({ duplicateDirection })
          }
          onPickedChange={(duplicatePicked) => patch({ duplicatePicked })}
          compact={compact}
        />
      ),
    });
  }

  if (decision.showRecategorize) {
    // Názvy vybraných kategórií, ktoré sa zachovajú inak - do vysvetliviek pod
    // výberom. Berú sa z `categories`, nie zo `siblings`: nález v tej kategórii
    // môže mať iný názov v čase, keď ho presunuli.
    const nameOf = (categoryId: string) =>
      categories.find((c) => c.category_id === categoryId)?.name ?? categoryId;
    const selectedTaken = state.categoryIds
      .filter((id) => takenCategoryIds.includes(id))
      .map(nameOf);
    const selectedMoved = state.categoryIds
      .filter((id) => movedCategoryIds.includes(id))
      .map(nameOf);

    // Výber kategórií je vlastný krok bez linky: patrí k prepínaču nad ním,
    // nie je to ďalšie rozhodnutie. Na desktope je v tej istej karte.
    const picker = state.recategorize && (
      <Stack
        gap={1}
        sx={{
          p: 1.625,
          borderRadius: 1,
          border: (theme) => `1px solid ${theme.palette.warning.surface}`,
          backgroundColor: 'warning.surface',
        }}
      >
        {/* Čo sa stane s pôvodnou kategóriou, závisí od prijatia: bez
                neho nález odchádza do stavu Presunuté. Kým tu bola veta
                „ostáva" napevno, hovorila pri vypnutom prijatí opak toho, čo
                sa naozaj stane. */}
        <Typography variant='caption' color='text.secondary'>
          {state.accept
            ? `${__('Pôvodná')} ${issue.category_name} ${__('v podnete ostáva.')}`
            : `${__('Nález v kategórii')} ${issue.category_name} ${__('sa presunie a u vás nezostane.')}`}
        </Typography>
        <Stack direction='row' gap={1} flexWrap='wrap'>
          {/* Ponúkajú sa **všetky** kategórie. Že sa niektorá zachová inak,
                  hovoria vysvetlivky pod výberom - schovať ju by znamenalo, že
                  úradník nevie, prečo tam nie je. */}
          {categories
            .filter((c) => c.category_id !== issue.category_id)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((category) => {
              const selected = state.categoryIds.includes(category.category_id);

              return (
                <DecisionChip
                  key={category.category_id}
                  selected={selected}
                  onClick={() =>
                    patch({
                      categoryIds: selected
                        ? state.categoryIds.filter(
                            (id) => id !== category.category_id,
                          )
                        : [...state.categoryIds, category.category_id],
                    })
                  }
                >
                  {category.name}
                </DecisionChip>
              );
            })}
        </Stack>

        {/* Vysvetlivky k tomu, čo úradník naklikal. Sú tu a nie v pätičke,
                lebo sa vzťahujú ku konkrétnym chipom nad nimi. */}
        {!!selectedTaken.length && (
          <Typography variant='caption' color='text.secondary'>
            {`${
              selectedTaken.length === 1
                ? __('V kategórii')
                : __('V kategóriách')
            } ${selectedTaken.join(', ')} ${__('nález už je - nový tam nevznikne.')}`}
          </Typography>
        )}

        {!!selectedMoved.length && (
          <Stack
            gap={0.5}
            sx={{
              p: 1.25,
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.info.surface}`,
              backgroundColor: 'background.paper',
            }}
          >
            <Typography variant='body2' color='info.dark' fontWeight={500}>
              {`${
                selectedMoved.length === 1
                  ? __('Z kategórie')
                  : __('Z kategórií')
              } ${selectedMoved.join(', ')} ${__('už nález raz presunuli')}`}
            </Typography>
            {/* Slučku nechceme: kategórie by si podnet mohli prehadzovať
                    dokola. Cesta späť preto vedie cez ruky toho, komu práca
                    pribudne - a ten sa musí rozhodnúť sám. */}
            <Typography variant='caption' color='info.dark'>
              {__(
                'Priradiť ju znova sa nedá. Pošlite kolegovi, ktorý ju má na starosti, identifikátor tohto nálezu - zadá ho vo svojom presunutom náleze cez „Znovu prijať nález" a podnet sa mu vráti ako podaný.',
              )}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                fontFamily: 'ui-monospace, Menlo, monospace',
                fontWeight: 700,
                userSelect: 'all',
              }}
            >
              {issue.id}
            </Typography>
          </Stack>
        )}
      </Stack>
    );

    steps.push({
      key: 'recategorize',
      divided: true,
      desktop: (
        <IssueSection
          title={__('Prekategorizovať nález')}
          description={__('Nález sa zaradí aj do ďalších kategórií.')}
          control={
            <Switch
              checked={state.recategorize}
              onChange={(_event, recategorize) =>
                patch({ recategorize, categoryIds: [] })
              }
              inputProps={{ 'aria-label': __('Prekategorizovať nález') }}
              sx={PRIMARY_SWITCH_SX}
            />
          }
        >
          {picker || undefined}
        </IssueSection>
      ),
      node: (
        <DecisionSwitch
          stacked={compact}
          label={__('Prekategorizovať nález')}
          hint={__('Nález sa zaradí aj do ďalších kategórií')}
          checked={state.recategorize}
          onChange={(recategorize) => patch({ recategorize, categoryIds: [] })}
        />
      ),
    });

    if (picker) {
      steps.push({ key: 'recategorize-picker', node: picker, desktop: null });
    }
  }

  if (showStatementForm) {
    steps.push({
      key: 'statement',
      divided: true,
      // Pätička rozhodnutia je pätičkou tejto karty (návrh 1a): vyriešenie
      // je voľba o tom, čím vyjadrenie je, a tlačidlo ukladá všetko naraz.
      desktop: (
        <IssueSection
          title={__('Vyjadrenie k nálezu')}
          description={decision.statementHint}
          footer={decisionFooter(
            decision.showResolve && (
              <Stack
                component='label'
                direction='row'
                alignItems='center'
                gap={1}
                sx={{ cursor: 'pointer' }}
              >
                <Checkbox
                  checked={state.resolve}
                  onChange={(_event, resolve) => patch({ resolve })}
                  sx={{ p: 0 }}
                />
                <Typography variant='body2'>
                  {__('Zároveň označiť nález ako vyriešený')}
                </Typography>
              </Stack>
            ),
          )}
        >
          <IssueStatementSection
            showHeading={false}
            hint={decision.statementHint}
            text={state.statementText}
            onTextChange={(statementText) => patch({ statementText })}
            files={files}
            onFilesChange={setFiles}
          />
        </IssueSection>
      ),
      node: (
        <IssueStatementSection
          hint={decision.statementHint}
          resolveSlot={
            decision.showResolve && (
              <DecisionSwitch
                framed
                stacked={compact}
                label={__('Označiť nález ako vyriešený')}
                hint={
                  state.resolve
                    ? __('Nález sa uzavrie, späť sa otvoriť nedá')
                    : __('Vyjadrenie sa zverejní, nález zostane otvorený')
                }
                checked={state.resolve}
                onChange={(resolve) => patch({ resolve })}
              />
            )
          }
          text={state.statementText}
          onTextChange={(statementText) => patch({ statementText })}
          files={files}
          onFilesChange={setFiles}
          compact={compact}
        />
      ),
    });
  }

  const body = (
    <Stack
      gap={1.75}
      sx={{
        px: compact ? 0 : 2.25,
        pt: compact ? 0 : 1.5,
        pb: compact ? 0 : 1.75,
      }}
    >
      {steps.map((step, index) => (
        <Box
          key={step.key}
          sx={
            // Riešiteľ nie je krok rozhodovania, takže ho od neho oddeľuje
            // linka vždy - aj keď nasledujúci krok inak linku nemá.
            index > 0 && (step.divided || steps[index - 1].key === 'assignee')
              ? {
                  pt: 1.625,
                  borderTop: (theme) =>
                    `1px solid ${theme.palette.dividerLight}`,
                }
              : undefined
          }
        >
          {step.node}
        </Box>
      ))}
    </Stack>
  );

  const footer = decision.showFooter && (
    <Stack
      gap={1.25}
      sx={{
        px: compact ? 1.75 : 2.25,
        py: compact ? 1.5 : 1.75,
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'background.default',
        // Žiadny záporný margin: pätička je priamym dieťaťom obalu bez
        // paddingu, takže by ju o 14 px vytlačil za okraj obrazovky a stránka
        // by sa dala skrolovať vodorovne.
        ...(compact && {
          position: 'sticky',
          bottom: 0,
          mt: 'auto',
        }),
      }}
    >
      {compact && (
        <Typography variant='caption' color='text.secondary'>
          {decision.sentences.join(' ')}
        </Typography>
      )}

      <Stack
        direction={compact ? 'column' : 'row'}
        alignItems={compact ? 'stretch' : 'center'}
        gap={1.5}
      >
        <Button
          fullWidth={compact}
          size={compact ? 'large' : 'medium'}
          disabled={decision.disabled || decideMutation.isPending}
          onClick={submitDecision}
        >
          {/* `Button` má `white-space: nowrap`; na 390 px sa dlhý zložený
              popis nezmestí, tak ho nesie text nad tlačidlom. */}
          {compact && decision.label.length > 30
            ? __('Potvrdiť zmeny')
            : decision.label}
        </Button>

        {!compact && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ maxWidth: '70ch' }}
          >
            {decision.sentences.join(' ')}
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  // História vyjadrení **pod** rozhodovaním a zbalená. Kým stála nad
  // formulárom a rozbalená, nález s pár kolami schvaľovania zaberal obrazovky
  // textu, ktorý úradník už čítal, a formulár odtlačila pod okraj. Najnovšie
  // vyjadrenie v schvaľovaní aj tak ukazuje `StatementApproval` hore.
  const history = !!timeline.length && (
    <Box
      sx={{
        px: compact ? 0 : 2.25,
        py: compact ? 0 : 1.25,
        ...(!compact && {
          borderTop: (theme: Theme) =>
            `1px solid ${theme.palette.dividerLight}`,
        }),
      }}
    >
      <IssueStatementHistory
        statements={timeline}
        canEdit={canEditStatement}
        onEdit={setRevising}
        canDelete={canEditStatement}
        onDelete={setDeleting}
      />
    </Box>
  );

  // Desktop (návrh 1a): sekcie ako samostatné karty na sivom podklade.
  const desktopSections = steps.map((step) => {
    if (step.desktop === null) return null;
    if (step.desktop) {
      return <React.Fragment key={step.key}>{step.desktop}</React.Fragment>;
    }
    if (step.bare) {
      return <Box key={step.key}>{step.node}</Box>;
    }
    return <IssueSection key={step.key}>{step.node}</IssueSection>;
  });

  // Rozhodnutie bez formulára vyjadrenia (napr. len prijatie či
  // prekategorizovanie v predvolenej kategórii) má pätičku vo vlastnej karte.
  const desktopFooter = decision.showFooter && !showStatementForm && (
    <IssueSection footer={decisionFooter()} />
  );

  const desktopHistory = !!timeline.length && (
    <IssueSection flush>
      <IssueStatementHistory
        statements={timeline}
        canEdit={canEditStatement}
        onEdit={setRevising}
        canDelete={canEditStatement}
        onDelete={setDeleting}
        inset
      />
    </IssueSection>
  );

  const revisionDialog = (
    <>
      <StatementRevisionDialog
        statement={revising}
        requiresApproval={membership.requiresApproval}
        onClose={() => setRevising(null)}
        onDone={() => Promise.all([refetchStatements(), onDone()])}
      />
      <ConfirmationDialog
        open={!!deleting}
        title={
          deleting?.final
            ? __('Odstrániť finálne vyjadrenie?')
            : __('Odstrániť vyjadrenie?')
        }
        description={[
          __(
            'Občan ho prestane vidieť. V histórii vyjadrení zostane prečiarknuté s vaším menom a časom odstránenia.',
          ),
          // API vyriešený nález bez finálneho stanoviska vráti do riešenia -
          // `final` a `resolved` sú tam jedna vec. Úradník to musí vedieť
          // vopred: lehota zase beží a podnet sa občanovi ukáže ako otvorený.
          reopensIssue
            ? __(
                'Nález sa vráti do stavu V riešení a uzavriete ho až novým finálnym vyjadrením. Občan správu nedostane, pri podnete však uvidí, že sa znova rieši.',
              )
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
        confirmLabel={__('Odstrániť vyjadrenie')}
        isLoading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting)}
        onClose={() => setDeleting(null)}
      />
    </>
  );

  // Odstránenie z kategórie (moj-presov#25) - na úplnom spodku karty, mimo
  // rozhodovania aj histórie, ako archivácia na spodku obálky. Len nad živým
  // nálezom, ktorý úradník smie meniť; oprávnenie overuje komponent.
  // V predvolenej kategórii sa neponúka vôbec: tam sa podnet neprijíma, len
  // posiela ďalej, a na to je prekategorizovanie.
  const removal = !archived &&
    !unmanaged &&
    !isDefaultCategory &&
    (issue.status === 'open' || issue.status === 'accepted') && (
      // Na desktope je to vlastná karta s červeným rámom (kreslí ju
      // `IssueRemove`), na mobile posledný krok obrazovky.
      <IssueRemove
        issue={issue}
        siblings={siblings}
        onDone={onDone}
        statementInReview={!!issue.statement_in_review_id}
        hasDuplicates={mergedIntoThis.length > 0}
        compact={compact}
      />
    );

  if (compact) {
    return (
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        <Stack
          gap={1.75}
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.75 }}
        >
          {assigneeLine}
          {body}
          {history}
          {removal}
        </Stack>
        {footer}
        {revisionDialog}
      </Stack>
    );
  }

  // Karta nálezu. Neskroluje vo svojom vnútri - skroluje stĺpec, v ktorom stoja
  // všetky karty pod sebou. Kým bol nález pravou polovicou dvojstĺpca, mal
  // vlastný skrolovací obal a hlavička nad ním stála napevno; s kartami by to
  // znamenalo toľko skrolovacích plôch, koľko je kategórií.
  return (
    <Stack
      sx={{
        flex: 'none',
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      {header}
      {assigneeLine}
      {/* `unmountOnExit` zámerne **nie je**: telo drží rozpísané vyjadrenie
          a vybrané prepínače, a tie by sa zbalením karty stratili. */}
      <Collapse in={expanded}>
        <Stack
          gap={2}
          // Bez vlastnej hornej linky - tú kreslí hlavička karty, kým je
          // rozbalená; dve pod sebou vyzerali ako zdvojený rám.
          sx={{ p: 2.5, backgroundColor: 'grey.50' }}
        >
          {desktopSections}
          {desktopFooter}
          {desktopHistory}
          {removal}
        </Stack>
      </Collapse>
      {revisionDialog}
    </Stack>
  );
};

export default IssueDetail;
